import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { MainSection } from '@/components/layout/MainSection';
import { SectionContent } from '@/components/layout/SectionContent';
import { TournamentMatchPreviewCard } from '@/components/TournamentMatchPreviewCard';
import { useMatchOverviewContext } from '@/layouts/MatchOverviewLayout/context';
import type { MatchTournamentsResponse } from '@/types/matchOverview';
// Same tournaments-list chrome as the league page's Tournaments tab.
import styles from '../LeagueDetailPage.module.scss';

/**
 * `/match/:matchId/tournaments` — where this match can be predicted: the
 * user's own tournaments containing it (the tournament Matches tab card with
 * their prediction / points, under the tournament name), then public
 * tournaments they can join, and a create-in-this-league CTA when there is
 * none. Every card opens the match inside its tournament.
 */
const TournamentsTab: React.FC = () => {
  const { match, league, matchId } = useMatchOverviewContext();
  const { t } = useTranslation('match');

  const { data, isLoading, error } = useQuery<MatchTournamentsResponse>({
    queryKey: ['match-tournaments', matchId],
    queryFn: async () => (await apiClient.getMatchTournaments(matchId)).data.data,
    enabled: !!matchId,
    // Predictions are made on the tournament-scoped match page; coming back
    // here must show them, whatever mutation path wrote them.
    refetchOnMount: 'always',
  });

  const mine = data?.mine ?? [];
  const publicTournaments = data?.public ?? [];
  const isEmpty = mine.length === 0 && publicTournaments.length === 0;

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('match:errorLoadingTournaments')}</p>;
  } else if (isLoading) {
    body = null;
  } else if (isEmpty) {
    body = (
      <SectionContent>
        <div className={styles.emptyState} data-testid="match-tournaments-empty">
          <p className={styles.emptyStateText}>{t('match:noTournaments')}</p>
          <Link
            className="ls-btn"
            to={`/tournaments/create?leagueId=${league.id}`}
            data-testid="match-tournaments-create"
          >
            {t('match:createTournament')}
          </Link>
        </div>
      </SectionContent>
    );
  } else {
    body = (
      <SectionContent>
        {mine.length > 0 && (
          <section className={styles.tournamentsGroup} data-testid="match-tournaments-mine">
            <h3 className={styles.groupTitle}>{t('match:myTournaments')}</h3>
            <div className={styles.tournamentsList}>
              {mine.map(tournament => (
                <TournamentMatchPreviewCard
                  key={tournament.id}
                  tournament={tournament}
                  match={match}
                  prediction={tournament.matchPrediction}
                  testId={`match-tournament-mine-${tournament.id}`}
                />
              ))}
            </div>
          </section>
        )}

        {publicTournaments.length > 0 && (
          <section className={styles.tournamentsGroup} data-testid="match-tournaments-public">
            <h3 className={styles.groupTitle}>{t('match:publicTournaments')}</h3>
            <div className={styles.tournamentsList}>
              {publicTournaments.map(tournament => (
                <TournamentMatchPreviewCard
                  key={tournament.id}
                  tournament={tournament}
                  match={match}
                  testId={`match-tournament-public-${tournament.id}`}
                />
              ))}
            </div>
          </section>
        )}
      </SectionContent>
    );
  }

  return (
    <MainSection
      hideTitleOnDesktop
      subtitleHideOnMobile
      title={league.name}
      subtitle={t('match:tabTournaments')}
      logoUrl={league.logoUrl || undefined}
      logoAlt={league.name}
    >
      <div data-testid="match-tournaments" aria-busy={isLoading}>
        {body}
      </div>
    </MainSection>
  );
};

export default TournamentsTab;
